import {
  CloudIcon,
  CodeBracketIcon,
  ComputerDesktopIcon,
  EyeIcon,
  GlobeAltIcon,
  GlobeAmericasIcon,
  InformationCircleIcon,
  ServerStackIcon,
  ShieldCheckIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import * as psl from 'psl';
import React, { useState } from 'react';
import { useSearchParams } from 'react-router';

import { useTheme } from '@/app/ThemeContext';
import {
  BaseTag,
  Container,
  SoundRecognitionSearch as SoundRecognitionSearchIcon,
} from '@/components/chariot-ui';
import { CopyToClipboard } from '@/components/CopyToClipboard';
import { NoData } from '@/components/legacy-ui/NoData';
import { MarkdownPreview } from '@/components/markdown/MarkdownPreview';
import { OverflowText } from '@/components/OverflowText';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { StatusChip } from '@/components/ui/status-chip';
import { combineAssetNodeResults, isWebApplication } from '@/hooks/useAssets';
import { useGetFile } from '@/hooks/useFiles';
import { useGraphQuery } from '@/hooks/useGraphQuery';
import {
  TechnologiesTab,
  TechnologyAttributeUnion,
} from '@/sections/asset/components/tabs';
import { VerticalNavigationWithSubTabs } from '@/sections/asset/components/tabs';
import { WebApplicationEndpoints } from '@/sections/asset/components/tabs';
import { WebApplicationSitemap } from '@/sections/asset/components/tabs';
import {
  useEndpoints,
  useRelatedWebApplication,
} from '@/sections/asset/hooks/drawer';
import {
  MainTabWithSubTabs,
  TabOptionWithIcon,
} from '@/sections/asset/types/navigation.types';
import { isADObject as isADObjectFn } from '@/sections/asset/utils';
import { hasNoRestApiEndpoints } from '@/sections/asset/utils/hasNoRestApiEndpoints';
import { ADObject, Asset, CloudResource } from '@/types';
import { cn } from '@/utils/classname';
import { formatDate } from '@/utils/date.util';
import { getDisplayIdentifierWithFallback } from '@/utils/displayIdentifier.util';

interface TabConfig {
  label: string;
  value: string;
  icon?: React.ComponentType<{ className?: string }>;
  condition?: (asset: Asset) => boolean;
  subTabs?: SubTabConfig[]; // Optional for root-level tabs
  component?: (
    asset: Asset,
    technologies: TechnologyAttributeUnion[]
  ) => React.ReactElement | undefined; // Component for root-level tabs
}

interface SubTabConfig {
  label: string;
  value: string;
  icon?: React.ComponentType<{ className?: string }>;
  condition?: (asset: Asset) => boolean;
  component: (
    asset: Asset,
    technologies: TechnologyAttributeUnion[]
  ) => React.ReactElement | undefined;
}

// Asset type detection utilities
// eslint-disable-next-line react-refresh/only-export-components
export const isCloudResource = (asset: Asset): asset is CloudResource => {
  if (asset && asset.key) {
    // Check for explicit cloud resource keys
    if (
      asset.key.startsWith('#awsresource#') ||
      asset.key.startsWith('#azureresource#') ||
      asset.key.startsWith('#gcpresource#')
    ) {
      return true;
    }
  }

  // Check for cloud-related DNS patterns
  if (asset.dns && typeof asset.dns === 'string') {
    const dns = asset.dns.toLowerCase();

    // AWS patterns
    if (
      dns.includes('.amazonaws.com') ||
      dns.includes('.aws.amazon.com') ||
      dns.includes('.awsapps.com') ||
      dns.includes('.elasticbeanstalk.com') ||
      dns.includes('.cloudfront.net') ||
      dns.includes('.execute-api.') ||
      dns.includes('.s3.') ||
      dns.includes('.s3-website')
    ) {
      return true;
    }

    // Azure patterns
    if (
      dns.includes('.azure.com') ||
      dns.includes('.azurewebsites.net') ||
      dns.includes('.blob.core.windows.net') ||
      dns.includes('.cloudapp.azure.com') ||
      dns.includes('.azurecontainer.io') ||
      dns.includes('.azurecr.io')
    ) {
      return true;
    }

    // GCP patterns
    if (
      dns.includes('.googleapis.com') ||
      dns.includes('.googleusercontent.com') ||
      dns.includes('.cloudfunction.net') ||
      dns.includes('.appspot.com') ||
      dns.includes('.run.app')
    ) {
      return true;
    }
  }

  // Check URL for cloud patterns
  if (asset.url && typeof asset.url === 'string') {
    const url = asset.url.toLowerCase();
    if (
      url.includes('.amazonaws.com') ||
      url.includes('.azure.com') ||
      url.includes('.googleapis.com')
    ) {
      return true;
    }
  }

  // Check asname for cloud provider patterns
  if (asset.asname && typeof asset.asname === 'string') {
    const asname = asset.asname.toLowerCase();

    // Common cloud provider AS names
    if (
      asname.includes('amazon') ||
      asname.includes('aws') ||
      asname.includes('microsoft') ||
      asname.includes('azure') ||
      asname.includes('google') ||
      asname.includes('gcp') ||
      asname.includes('digitalocean') ||
      asname.includes('linode') ||
      asname.includes('vultr') ||
      asname.includes('oracle-cloud') ||
      asname.includes('alibaba') ||
      asname.includes('ibm-cloud') ||
      asname.includes('ovh') ||
      asname.includes('hetzner')
    ) {
      return true;
    }
  }

  // Check class field for cloud-related values
  if (asset.class && typeof asset.class === 'string') {
    const assetClass = asset.class.toLowerCase();
    if (
      assetClass === 'amazon' ||
      assetClass === 'aws' ||
      assetClass === 'azure' ||
      assetClass === 'gcp' ||
      assetClass === 'google' ||
      assetClass === 'cloud' ||
      assetClass === 'digitalocean' ||
      assetClass === 'linode' ||
      assetClass === 'vultr'
    ) {
      return true;
    }
  }

  // Check for cloud account information
  if (asset.cloudAccount || asset.cloudId || asset.cloudService) {
    return true;
  }

  return false;
};

const normalizeADObject = (asset: Asset): ADObject => {
  // This function should only be called for actual AD objects
  // If it's already a proper ADObject, return as-is
  if (isADObjectFn(asset)) {
    return asset as ADObject;
  }

  // This shouldn't happen - if we reach here, the asset is not an AD object
  // Return a minimal ADObject structure for safety (though this case should not occur)
  return {
    ...asset,
    name: asset.name || asset.dns || 'Unknown',
    key: asset.key || '',
    class: 'adcomputer' as ADObject['class'], // Default to computer class
    label: 'AD Computer' as ADObject['label'], // Use a valid label
    domain: '',
    objectid: asset.identifier || '',
    dns: asset.dns || '',
    displayname: asset.name || asset.dns || '',
    distinguishedname: '',
  } as ADObject;
};

const formatTTL = (ttlTimestamp: number): string => {
  if (!ttlTimestamp || ttlTimestamp === 0) {
    return 'Permanent';
  }

  const currentTimeSeconds = Math.floor(Date.now() / 1000);
  const remainingSeconds = ttlTimestamp - currentTimeSeconds;

  if (remainingSeconds <= 0) {
    return 'Expired';
  }

  const days = Math.floor(remainingSeconds / 86400);
  const years = Math.floor(days / 365);
  const formattedSeconds = remainingSeconds.toLocaleString();

  if (years > 0) {
    return `${formattedSeconds} seconds (≈ ${years.toFixed(1)} years)`;
  } else if (days > 0) {
    return `${formattedSeconds} seconds (≈ ${days} days)`;
  } else {
    return `${formattedSeconds} seconds`;
  }
};

const getStatusIndicator = (
  status: string
): { icon: string; color: string; label: string } => {
  const statusMap: Record<
    string,
    { icon: string; color: string; label: string }
  > = {
    A: { icon: '🟢', color: 'text-green-600', label: 'Active' },
    I: { icon: '🔴', color: 'text-red-600', label: 'Inactive' },
    U: { icon: '🟡', color: 'text-yellow-600', label: 'Unknown' },
  };

  return (
    statusMap[status] || {
      icon: '🟡',
      color: 'text-gray-500',
      label: status || 'Unknown',
    }
  );
};

function LinkedWebApplicationSection({
  webApplications,
  onOpen,
}: {
  webApplications: Asset[];
  onOpen: (webApplicationKey: string) => void;
}) {
  return (
    <div className="space-y-6">
      <div className="bg-chariot-layer0 border-chariot-border rounded-lg border p-4">
        <div className="space-y-3">
          <div>
            <h4 className="text-chariot-text-primary text-sm font-medium">
              Related Web Applications
            </h4>
            <p className="text-chariot-text-secondary mt-1 text-sm">
              This asset has the following web applications.
            </p>
          </div>

          {webApplications.map((webApplication, index) => (
            <div key={webApplication.key} className="space-y-3">
              <div className="border-chariot-border border-b" />

              <div className="flex items-start justify-between gap-3">
                <span className="text-chariot-text-secondary text-sm">
                  Name
                </span>
                <span className="text-chariot-text-primary max-w-[70%] text-right text-sm font-medium break-all">
                  {webApplication.name || webApplication.group || '-'}
                </span>
              </div>

              <div className="flex items-start justify-between gap-3">
                <span className="text-chariot-text-secondary text-sm">URL</span>
                <div className="flex max-w-[70%] items-center gap-2">
                  <span className="text-chariot-text-primary text-right text-sm font-medium break-all">
                    {(webApplication as { primary_url?: string }).primary_url ||
                      webApplication.identifier ||
                      '-'}
                  </span>
                  {((webApplication as { primary_url?: string }).primary_url ||
                    webApplication.identifier) && (
                    <CopyToClipboard
                      textToCopy={
                        (webApplication as { primary_url?: string })
                          .primary_url || webApplication.identifier
                      }
                      alwaysShowCopyIcon
                    />
                  )}
                </div>
              </div>

              <Button
                variant="outline"
                onClick={() => onOpen(webApplication.key)}
                className={`rounded-full px-3 py-1.5 text-sm${index === webApplications.length - 1 ? 'mt-2' : ''}`}
              >
                Open Web Application
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Network IP Connectivity Section Component
function NetworkIPConnectivitySection({ asset }: { asset: Asset }) {
  // Extract network-related properties
  const ipAddress = asset.name || asset.identifier;
  const dnsName = asset.dns;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const assetClass = (asset as any).class;
  const asName = asset.asname;
  const asNumber = asset.asnumber;

  // Determine if it's IPv4 or IPv6
  const isIPv4 = assetClass === 'ipv4';
  const isIPv6 = assetClass === 'ipv6';

  return (
    <div className="space-y-6">
      {/* Header with IP type indicator */}
      <div className="bg-chariot-layer1 border-chariot-border rounded-lg border p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-chariot-brand-primary/10 rounded-lg p-2">
              <GlobeAltIcon className="text-chariot-brand-primary h-5 w-5" />
            </div>
            <div>
              <p className="text-chariot-text-primary text-lg font-semibold">
                {isIPv4 ? 'IPv4' : isIPv6 ? 'IPv6' : 'Network'} Asset
              </p>
              {ipAddress && (
                <div className="mt-1 flex items-center gap-2">
                  <p className="text-chariot-text-primary font-mono text-base">
                    {ipAddress}
                  </p>
                  <CopyToClipboard textToCopy={ipAddress} alwaysShowCopyIcon />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Network Identity Card */}
      <div className="bg-chariot-layer0 border-chariot-border rounded-lg border">
        <div className="border-chariot-border border-b px-4 py-3">
          <h4 className="text-chariot-text-primary text-sm font-medium">
            Network Identity
          </h4>
        </div>
        <div className="space-y-3 p-4">
          <div className="flex items-start justify-between">
            <span className="text-chariot-text-secondary text-sm">
              DNS Name
            </span>
            <div className="flex items-center gap-2">
              <span className="text-chariot-text-primary max-w-xs text-right text-sm font-medium break-all">
                {dnsName || '-'}
              </span>
              {dnsName && (
                <CopyToClipboard textToCopy={dnsName} alwaysShowCopyIcon />
              )}
            </div>
          </div>

          <div className="border-chariot-border border-b"></div>

          <div className="flex items-start justify-between">
            <span className="text-chariot-text-secondary text-sm">
              IP Address
            </span>
            <div className="flex items-center gap-2">
              <span className="text-chariot-text-primary font-mono text-sm font-medium">
                {ipAddress || '-'}
              </span>
              {ipAddress && (
                <CopyToClipboard textToCopy={ipAddress} alwaysShowCopyIcon />
              )}
            </div>
          </div>

          <div className="border-chariot-border border-b"></div>

          <div className="flex items-start justify-between">
            <div className="flex items-center gap-1">
              <span className="text-chariot-text-secondary text-sm">
                IP Version
              </span>
              <InformationCircleIcon
                className="text-chariot-text-tertiary h-3.5 w-3.5 cursor-help"
                title="IPv4 uses 32-bit addresses (e.g., 192.168.1.1), IPv6 uses 128-bit addresses (e.g., 2001:db8::1)"
              />
            </div>
            <span className="bg-chariot-brand-primary/10 text-chariot-brand-primary rounded-md px-2 py-1 text-xs font-medium uppercase">
              {assetClass || '-'}
            </span>
          </div>
        </div>
      </div>

      {/* Network Context Card */}
      <div className="bg-chariot-layer0 border-chariot-border rounded-lg border">
        <div className="border-chariot-border border-b px-4 py-3">
          <h4 className="text-chariot-text-primary text-sm font-medium">
            Network Context
          </h4>
        </div>
        <div className="space-y-3 p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-1">
              <span className="text-chariot-text-secondary text-sm">
                AS Name
              </span>
              <InformationCircleIcon
                className="text-chariot-text-tertiary h-3.5 w-3.5 cursor-help"
                title="Autonomous System name - a collection of connected IP routing prefixes under the control of one or more network operators"
              />
            </div>
            <span className="text-chariot-text-primary text-sm font-medium">
              {asName || '-'}
            </span>
          </div>

          <div className="border-chariot-border border-b"></div>

          <div className="flex items-start justify-between">
            <div className="flex items-center gap-1">
              <span className="text-chariot-text-secondary text-sm">
                AS Number
              </span>
              <InformationCircleIcon
                className="text-chariot-text-tertiary h-3.5 w-3.5 cursor-help"
                title="A unique identifier allocated to an Autonomous System for use in BGP routing"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-chariot-text-primary text-sm font-medium">
                {asNumber || '-'}
              </span>
              {asNumber && (
                <CopyToClipboard textToCopy={asNumber} alwaysShowCopyIcon />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Network General Section Component
function NetworkGeneralSection({ asset }: { asset: Asset }) {
  // Extract network-related properties
  const status = asset.status;
  const created = asset.created;
  const visited = asset.visited;
  const ttl = asset.ttl;

  // Get status indicator
  const statusInfo = getStatusIndicator(status);

  return (
    <div className="space-y-6">
      {/* Asset Metadata Card */}
      <div className="bg-chariot-layer0 border-chariot-border rounded-lg border">
        <div className="border-chariot-border border-b px-4 py-3">
          <h4 className="text-chariot-text-primary text-sm font-medium">
            Asset Metadata
          </h4>
        </div>
        <div className="space-y-3 p-4">
          <div className="flex items-start justify-between">
            <span className="text-chariot-text-secondary text-sm">Status</span>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium ${statusInfo.color}`}>
                {statusInfo.icon} {statusInfo.label}
              </span>
            </div>
          </div>

          <div className="flex items-start justify-between">
            <div className="flex items-center gap-1">
              <span className="text-chariot-text-secondary text-sm">
                Time to Live (TTL)
              </span>
              <InformationCircleIcon
                className="text-chariot-text-tertiary h-3.5 w-3.5 cursor-help"
                title="Time until the object expires (refreshed every time Guard discovers it)"
              />
            </div>
            <span className="text-chariot-text-primary text-right text-sm font-medium">
              {formatTTL(ttl)}
            </span>
          </div>
        </div>
      </div>

      {/* Temporal Information Card */}
      <div className="bg-chariot-layer0 border-chariot-border rounded-lg border">
        <div className="border-chariot-border border-b px-4 py-3">
          <h4 className="text-chariot-text-primary text-sm font-medium">
            Temporal Information
          </h4>
        </div>
        <div className="space-y-3 p-4">
          {created && (
            <div className="flex items-start justify-between">
              <span className="text-chariot-text-secondary text-sm">
                First Seen
              </span>
              <span className="text-chariot-text-primary text-sm font-medium">
                {formatDate(created)}
              </span>
            </div>
          )}

          {visited && (
            <div className="flex items-start justify-between">
              <span className="text-chariot-text-secondary text-sm">
                Last Visited
              </span>
              <span className="text-chariot-text-primary text-sm font-medium">
                {formatDate(visited)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Tab configuration schemas
const TAB_CONFIGS: Record<string, TabConfig> = {
  activeDirectory: {
    label: 'Active Directory',
    value: 'ad',
    icon: ShieldCheckIcon,
    condition: (asset: Asset) => {
      // Enable ONLY for actual AD objects with AD-specific keys
      return isADObjectFn(asset);
    },
    component: (asset: Asset) => (
      <ActiveDirectorySection adObject={normalizeADObject(asset)} />
    ),
  },
  application: {
    label: 'Application',
    value: 'application',
    icon: GlobeAltIcon,
    condition: (asset: Asset) => isWebApplication(asset),
    subTabs: [
      {
        label: 'Sitemap',
        value: 'sitemap',
        icon: ServerStackIcon,
        component: (asset: Asset) => <WebApplicationSitemap asset={asset} />,
        condition: (asset: Asset) => isWebApplication(asset),
      },
      {
        label: 'REST API Endpoints',
        value: 'endpoints',
        icon: CodeBracketIcon,
        component: (asset: Asset) => <WebApplicationEndpoints asset={asset} />,
        condition: (asset: Asset) => isWebApplication(asset),
      },
    ],
  },
  cloudIdentity: {
    label: 'Cloud',
    value: 'cloud',
    icon: CloudIcon,
    condition: (asset: Asset) => isCloudResource(asset),
    component: (asset: Asset) => (
      <CloudIdentitySection asset={asset as CloudResource} />
    ),
  },
  ipConnectivity: {
    label: 'Network',
    value: 'network',
    icon: GlobeAltIcon,
    condition: (asset: Asset) => asset.key.startsWith('#asset'), // Show for network assets
    component: (asset: Asset) => <NetworkIPConnectivitySection asset={asset} />,
  },
  networkGeneral: {
    label: 'General',
    value: 'general',
    icon: TagIcon,
    condition: (asset: Asset) => asset.key.startsWith('#asset'), // Show for network assets
    component: (asset: Asset) => <NetworkGeneralSection asset={asset} />,
  },
  domain: {
    label: 'Domain',
    value: 'domain',
    icon: GlobeAmericasIcon,
    condition: (asset: Asset) => {
      if (!asset.key.startsWith('#asset')) return false; // Must be a network asset
      const entityGroup = asset.group || '';
      return entityGroup && typeof entityGroup === 'string'
        ? psl.isValid(entityGroup)
        : false;
    },
    component: (asset: Asset) => <AssetDomainSection asset={asset} />,
  },
  technologies: {
    label: 'Ports',
    value: 'technologies',
    icon: EyeIcon,
    condition: (asset: Asset) => asset.key.startsWith('#asset'), // Show for network assets
    component: (asset: Asset, technologies: TechnologyAttributeUnion[]) => (
      <TechnologiesTab technologies={technologies} />
    ),
  },
};

// Factory function to build tabs from configuration
const buildTabsFromConfig = (
  asset: Asset,
  technologies: TechnologyAttributeUnion[]
): MainTabWithSubTabs[] => {
  return Object.values(TAB_CONFIGS).map(config => {
    const isEnabled = config.condition ? config.condition(asset) : true;

    // Check if this is a root-level tab (no subTabs) or a parent tab (with subTabs)
    if (!config.subTabs) {
      // Root-level tab - create a single-tab structure
      return {
        label: config.label,
        value: config.value,
        icon: config.icon,
        disabled: isEnabled ? undefined : `${config.label} is not available`,
        tabs: [
          {
            label: config.label,
            value: config.value,
            icon: config.icon,
            component: config.component
              ? config.component(asset, technologies)
              : undefined,
            disabled: isEnabled
              ? undefined
              : `${config.label} is not available`,
          },
        ],
      };
    }

    // Parent tab with subtabs
    return {
      label: config.label,
      value: config.value,
      icon: config.icon,
      disabled: isEnabled ? undefined : `${config.label} is not available`,
      tabs: config.subTabs.map(subTab => {
        const isSubTabEnabled = subTab.condition
          ? subTab.condition(asset)
          : true;

        return {
          label: subTab.label,
          value: subTab.value,
          icon: subTab.icon,
          component: subTab.component(asset, technologies),
          disabled: isSubTabEnabled
            ? undefined
            : `${subTab.label} is not available`,
        };
      }),
    };
  });
};

export function AttributesTabContent({
  asset,
  technologies,
}: {
  asset: Asset;
  technologies: TechnologyAttributeUnion[];
}) {
  'use memo';

  // Create a storage key specific to this asset
  const storageKey = `attributes-tab-${asset.key}`;
  const [, setSearchParams] = useSearchParams();

  const [openMainTabs, setOpenMainTabs] = useState<Set<string>>(new Set());
  const [selectedSubTab, setSelectedSubTab] = useState<
    TabOptionWithIcon | undefined
  >();

  const { relatedWebApplications } = useRelatedWebApplication(asset);

  // Gray out the "REST API Endpoints" sub-tab when the asset's OpenAPI spec
  // resolved with nothing to show. Parse errors / oversized specs stay enabled —
  // the tab still surfaces the error and the spec download. Gated to web apps,
  // the only place the sub-tab appears.
  const {
    endpoints: restApiEndpoints,
    status: endpointsStatus,
    error: endpointsError,
  } = useEndpoints(asset);
  const hasNoRestEndpoints = hasNoRestApiEndpoints(
    asset,
    restApiEndpoints,
    endpointsStatus,
    endpointsError
  );

  const openRelatedWebApplication = (webApplicationKey: string) => {
    setSearchParams(searchParam => {
      searchParam.set('assetDrawerKey', webApplicationKey);
      searchParam.delete('assetDrawerTab');
      searchParam.delete('assetDrawerSubTab');
      return searchParam;
    });
  };

  const baseTabs = buildTabsFromConfig(asset, technologies);
  const mainTabs = baseTabs.map(tab => {
    if (
      tab.value !== 'application' ||
      relatedWebApplications.length === 0 ||
      isWebApplication(asset)
    ) {
      // A web application's own Application tab keeps its real sub-tabs
      // (Sitemap + REST API Endpoints); gray out Endpoints when it has none.
      if (tab.value === 'application' && hasNoRestEndpoints) {
        return {
          ...tab,
          tabs: tab.tabs.map(subTab =>
            subTab.value === 'endpoints'
              ? { ...subTab, disabled: 'REST API Endpoints is not available' }
              : subTab
          ),
        };
      }
      return tab;
    }

    return {
      ...tab,
      disabled: undefined,
      tabs: [
        {
          label: 'Application',
          value: 'related-application',
          icon: GlobeAltIcon,
          component: (
            <LinkedWebApplicationSection
              webApplications={relatedWebApplications}
              onOpen={openRelatedWebApplication}
            />
          ),
          disabled: undefined,
        },
      ],
    };
  });

  const isNoTabsAvailable = mainTabs.every(tab => tab.disabled);

  // Load persisted tab selection or auto-select first tab
  React.useEffect(() => {
    if (!isNoTabsAvailable) {
      // Try to restore the previous selection from localStorage
      const savedSelection = localStorage.getItem(storageKey);
      let restoredTab = false;

      if (savedSelection) {
        try {
          const { mainTabValue, subTabValue } = JSON.parse(savedSelection);

          // Find the main tab
          const mainTab = mainTabs.find(
            tab => tab.value === mainTabValue && !tab.disabled
          );
          if (mainTab) {
            // Find the sub tab
            const subTab = mainTab.tabs.find(
              tab => tab.value === subTabValue && !tab.disabled
            );
            if (subTab) {
              setOpenMainTabs(new Set([mainTab.value]));
              setSelectedSubTab(subTab);
              restoredTab = true;
            }
          }
        } catch {
          // Invalid saved data, fallback to default
          localStorage.removeItem(storageKey);
        }
      }

      // If no valid saved selection, select the first available tab
      if (!restoredTab) {
        const firstEnabledMainTab = mainTabs.find(tab => !tab.disabled);
        if (firstEnabledMainTab && firstEnabledMainTab.tabs.length > 0) {
          const firstSubTab = firstEnabledMainTab.tabs[0];
          setOpenMainTabs(new Set([firstEnabledMainTab.value]));

          const firstEnabledSubTab = firstEnabledMainTab.tabs.find(
            tab => !tab.disabled
          );

          setSelectedSubTab(firstEnabledSubTab || firstSubTab);
        }
      }
    } else {
      setSelectedSubTab(undefined);
    }
  }, [asset.key, isNoTabsAvailable, mainTabs, storageKey]);

  const handleMainTabSelect = (tab: MainTabWithSubTabs) => {
    setOpenMainTabs(prevOpenTabs => {
      const newOpenTabs = new Set(prevOpenTabs);
      if (newOpenTabs.has(tab.value)) {
        newOpenTabs.delete(tab.value);
      } else {
        newOpenTabs.add(tab.value);
      }
      return newOpenTabs;
    });
  };

  // Save the tab selection whenever it changes
  const handleSubTabSelect = (subTab: TabOptionWithIcon) => {
    setSelectedSubTab(subTab);

    // Find the main tab that contains this sub tab
    const mainTab = mainTabs.find(tab =>
      tab.tabs.some(t => t.value === subTab.value)
    );

    if (mainTab) {
      // Save to localStorage
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          mainTabValue: mainTab.value,
          subTabValue: subTab.value,
        })
      );
    }
  };

  return (
    <Container
      className="size-full overflow-hidden p-0"
      tabIndex={-1}
      id="attributes"
    >
      <div className="flex size-full">
        <div className="border-chariot-stroke w-48 shrink-0 overflow-y-auto border-r">
          <VerticalNavigationWithSubTabs
            mainTabs={mainTabs}
            openMainTabs={openMainTabs}
            selectedSubTab={selectedSubTab}
            onMainTabSelect={handleMainTabSelect}
            onSubTabSelect={handleSubTabSelect}
          />
        </div>

        <div
          className="flex-1 overflow-y-auto px-6 py-4"
          data-testid="vertical-nav-content"
        >
          {selectedSubTab ? (
            <div className="flex h-full flex-col gap-6">
              <div className="border-chariot-stroke pb-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-chariot-text-primary text-xl font-semibold">
                    {selectedSubTab.label}
                  </h2>
                  {selectedSubTab.value === 'endpoints' && (
                    <StatusChip
                      label="Discovered by vespasian"
                      variant="info"
                    />
                  )}
                </div>
                <p className="text-chariot-text-secondary text-sm">
                  {selectedSubTab.label} attributes and configuration details
                </p>
              </div>
              {selectedSubTab.component}
            </div>
          ) : isNoTabsAvailable ? (
            <NoData
              icon={
                <SoundRecognitionSearchIcon className="text-chariot-stroke text-[100px]" />
              }
              title="No attributes available"
            />
          ) : (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <h3 className="text-chariot-text-primary text-lg font-semibold">
                  No sub-tab selected
                </h3>
                <p className="text-chariot-text-secondary mt-2 text-sm">
                  Expand categories and click on a sub-tab to view attributes
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Container>
  );
}

// Property display component
interface PropertyItem {
  label: string;
  value: string | number | boolean | null | undefined;
  copyable?: boolean;
  isBoolean?: boolean;
}

const PropertyDisplay = ({
  label,
  value,
  copyable = true,
  isBoolean,
}: PropertyItem) => {
  const isBooleanValue =
    typeof value === 'boolean' || value === 'false' || value === 'true';
  if (!isBoolean && !isBooleanValue && !value) return null;

  return (
    <div className="grid grid-cols-3 gap-4 py-2">
      <span className="text-chariot-text-secondary col-span-1 text-sm font-medium break-words">
        {label}
      </span>
      {isBoolean || isBooleanValue ? (
        <BaseTag
          className={cn(
            'h-5 rounded-md py-0 font-medium',
            value
              ? 'border-chariot-stroke-info bg-chariot-status-info text-chariot-text-primary'
              : 'border-chariot-stroke-critical bg-chariot-status-critical text-chariot-text-critical'
          )}
          size="xs"
        >
          {value ? '✓ Yes' : '✗ No'}
        </BaseTag>
      ) : copyable && value ? (
        <CopyToClipboard className="col-span-2" textToCopy={String(value)}>
          <OverflowText text={String(value)} />
        </CopyToClipboard>
      ) : (
        <span className="text-chariot-text-primary col-span-2 font-mono text-sm">
          {String(value)}
        </span>
      )}
    </div>
  );
};

// Comprehensive Active Directory Section
function ActiveDirectorySection({ adObject }: { adObject: ADObject }) {
  // Extract object type for header
  const objectType = adObject.label || 'AD Object';
  const objectName = adObject.name || adObject.displayname || 'Unknown';

  return (
    <div className="space-y-6">
      {/* Header with AD object type indicator */}
      <div className="bg-chariot-layer1 border-chariot-border rounded-lg border p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-chariot-brand-primary/10 rounded-lg p-2">
              <ComputerDesktopIcon className="text-chariot-brand-primary h-5 w-5" />
            </div>
            <div>
              <p className="text-chariot-text-primary text-lg font-semibold">
                {objectType}
              </p>
              {objectName && (
                <div className="mt-1 flex items-center gap-2">
                  <p className="text-chariot-text-primary font-mono text-base">
                    {objectName}
                  </p>
                  <CopyToClipboard textToCopy={objectName} alwaysShowCopyIcon />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* AD Identity Card */}
      <div className="bg-chariot-layer0 border-chariot-border rounded-lg border">
        <div className="border-chariot-border border-b px-4 py-3">
          <h4 className="text-chariot-text-primary text-sm font-medium">
            AD Identity
          </h4>
        </div>
        <div className="space-y-3 p-4">
          {adObject.displayname && (
            <div className="flex items-start justify-between">
              <span className="text-chariot-text-secondary text-sm">
                Display Name
              </span>
              <div className="flex items-center gap-2">
                <span className="text-chariot-text-primary max-w-xs text-right text-sm font-medium break-all">
                  {adObject.displayname}
                </span>
                <CopyToClipboard
                  textToCopy={adObject.displayname}
                  alwaysShowCopyIcon
                />
              </div>
            </div>
          )}

          {adObject.displayname && adObject.objectid && (
            <div className="border-chariot-border border-b"></div>
          )}

          {adObject.objectid && (
            <div className="flex items-start justify-between">
              <span className="text-chariot-text-secondary text-sm">
                Object ID
              </span>
              <div className="flex items-center gap-2">
                <span className="text-chariot-text-primary font-mono text-sm text-xs font-medium">
                  {adObject.objectid}
                </span>
                <CopyToClipboard
                  textToCopy={adObject.objectid}
                  alwaysShowCopyIcon
                />
              </div>
            </div>
          )}

          {adObject.objectid && adObject.sid && (
            <div className="border-chariot-border border-b"></div>
          )}

          {adObject.sid && (
            <div className="flex items-start justify-between">
              <span className="text-chariot-text-secondary text-sm">SID</span>
              <div className="flex items-center gap-2">
                <span className="text-chariot-text-primary font-mono text-sm text-xs font-medium">
                  {adObject.sid}
                </span>
                <CopyToClipboard textToCopy={adObject.sid} alwaysShowCopyIcon />
              </div>
            </div>
          )}

          {adObject.sid && adObject.samaccountname && (
            <div className="border-chariot-border border-b"></div>
          )}

          {adObject.samaccountname && (
            <div className="flex items-start justify-between">
              <span className="text-chariot-text-secondary text-sm">
                SAM Account Name
              </span>
              <div className="flex items-center gap-2">
                <span className="text-chariot-text-primary text-sm font-medium">
                  {adObject.samaccountname}
                </span>
                <CopyToClipboard
                  textToCopy={adObject.samaccountname}
                  alwaysShowCopyIcon
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* AD Domain & Organization Card */}
      <div className="bg-chariot-layer0 border-chariot-border rounded-lg border">
        <div className="border-chariot-border border-b px-4 py-3">
          <h4 className="text-chariot-text-primary text-sm font-medium">
            Domain & Organization
          </h4>
        </div>
        <div className="space-y-3 p-4">
          {adObject.domain && (
            <div className="flex items-start justify-between">
              <span className="text-chariot-text-secondary text-sm">
                Domain
              </span>
              <span className="text-chariot-text-primary text-sm font-medium">
                {adObject.domain}
              </span>
            </div>
          )}

          {adObject.domain && adObject.domainfqdn && (
            <div className="border-chariot-border border-b"></div>
          )}

          {adObject.domainfqdn && (
            <div className="flex items-start justify-between">
              <span className="text-chariot-text-secondary text-sm">
                Domain FQDN
              </span>
              <span className="text-chariot-text-primary text-sm font-medium">
                {adObject.domainfqdn}
              </span>
            </div>
          )}

          {adObject.dnshostname && (
            <div className="flex items-start justify-between">
              <span className="text-chariot-text-secondary text-sm">
                DNS Hostname
              </span>
              <span className="text-chariot-text-primary max-w-xs text-right text-sm font-medium break-all">
                {adObject.dnshostname}
              </span>
            </div>
          )}

          {adObject.department && (
            <div className="flex items-start justify-between">
              <span className="text-chariot-text-secondary text-sm">
                Department
              </span>
              <span className="text-chariot-text-primary text-sm font-medium">
                {adObject.department}
              </span>
            </div>
          )}

          {(adObject.domain ||
            adObject.domainfqdn ||
            adObject.dnshostname ||
            adObject.department) &&
            adObject.distinguishedname && (
              <div className="border-chariot-border border-b"></div>
            )}

          {adObject.distinguishedname && (
            <div className="flex items-start justify-between">
              <span className="text-chariot-text-secondary text-sm">
                Distinguished Name
              </span>
              <div className="flex items-center gap-2">
                <span className="text-chariot-text-primary max-w-xs text-right text-sm text-xs font-medium break-all">
                  {adObject.distinguishedname}
                </span>
                <CopyToClipboard
                  textToCopy={adObject.distinguishedname}
                  alwaysShowCopyIcon
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* AD Security Context Card */}
      <div className="bg-chariot-layer0 border-chariot-border rounded-lg border">
        <div className="border-chariot-border border-b px-4 py-3">
          <h4 className="text-chariot-text-primary text-sm font-medium">
            Security Context
          </h4>
        </div>
        <div className="space-y-3 p-4">
          <div className="flex items-start justify-between">
            <span className="text-chariot-text-secondary text-sm">
              Admin Count
            </span>
            <span className="text-chariot-text-primary text-sm font-medium">
              {adObject.adminCount || '0'}
            </span>
          </div>

          <div className="border-chariot-border border-b"></div>

          <div className="flex items-start justify-between">
            <span className="text-chariot-text-secondary text-sm">
              Sensitive Account
            </span>
            <BaseTag
              className={cn(
                'h-5 rounded-md py-0 font-medium',
                adObject.sensitive
                  ? 'border-chariot-stroke-info bg-chariot-status-info text-chariot-text-primary'
                  : 'border-chariot-stroke bg-chariot-layer1 text-chariot-text-secondary'
              )}
              size="xs"
            >
              {adObject.sensitive ? '✓ Yes' : '✗ No'}
            </BaseTag>
          </div>

          <div className="border-chariot-border border-b"></div>

          <div className="flex items-start justify-between">
            <span className="text-chariot-text-secondary text-sm">Deleted</span>
            <BaseTag
              className={cn(
                'h-5 rounded-md py-0 font-medium',
                adObject.isdeleted
                  ? 'border-chariot-stroke-critical bg-chariot-status-critical text-chariot-text-critical'
                  : 'border-chariot-stroke bg-chariot-layer1 text-chariot-text-secondary'
              )}
              size="xs"
            >
              {adObject.isdeleted ? '✓ Yes' : '✗ No'}
            </BaseTag>
          </div>

          {adObject.hasLaps !== undefined && (
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-1">
                <span className="text-chariot-text-secondary text-sm">
                  Has LAPS
                </span>
                <InformationCircleIcon
                  className="text-chariot-text-tertiary h-3.5 w-3.5 cursor-help"
                  title="Local Administrator Password Solution - Manages local admin passwords"
                />
              </div>
              <BaseTag
                className={cn(
                  'h-5 rounded-md py-0 font-medium',
                  adObject.hasLaps
                    ? 'border-chariot-stroke-success bg-chariot-status-success text-chariot-text-primary'
                    : 'border-chariot-stroke bg-chariot-layer1 text-chariot-text-secondary'
                )}
                size="xs"
              >
                {adObject.hasLaps ? '✓ Enabled' : '✗ Disabled'}
              </BaseTag>
            </div>
          )}
        </div>
      </div>

      {/* AD Authentication Card */}
      <div className="bg-chariot-layer0 border-chariot-border rounded-lg border">
        <div className="border-chariot-border border-b px-4 py-3">
          <h4 className="text-chariot-text-primary text-sm font-medium">
            Authentication Settings
          </h4>
        </div>
        <div className="space-y-3 p-4">
          {adObject.lastLogon && (
            <div className="flex items-start justify-between">
              <span className="text-chariot-text-secondary text-sm">
                Last Logon
              </span>
              <span className="text-chariot-text-primary text-sm font-medium">
                {formatDate(
                  new Date(
                    adObject.lastLogon / 10000 - 11644473600000
                  ).toISOString()
                )}
              </span>
            </div>
          )}

          {adObject.lastLogonTimestamp && (
            <div className="flex items-start justify-between">
              <span className="text-chariot-text-secondary text-sm">
                Last Logon Timestamp
              </span>
              <span className="text-chariot-text-primary text-sm font-medium">
                {formatDate(
                  new Date(
                    adObject.lastLogonTimestamp / 10000 - 11644473600000
                  ).toISOString()
                )}
              </span>
            </div>
          )}

          {(adObject.lastLogon || adObject.lastLogonTimestamp) && (
            <div className="border-chariot-border border-b"></div>
          )}

          <div className="grid grid-cols-2 gap-3">
            {adObject.passwordNeverExpires !== undefined && (
              <div className="flex items-center justify-between">
                <span className="text-chariot-text-secondary text-xs">
                  Pwd Never Expires
                </span>
                <BaseTag
                  className={cn(
                    'h-4 rounded-md py-0 text-xs font-medium',
                    adObject.passwordNeverExpires
                      ? 'border-chariot-stroke-warning bg-chariot-status-warning text-chariot-text-primary'
                      : 'border-chariot-stroke bg-chariot-layer1 text-chariot-text-secondary'
                  )}
                  size="xs"
                >
                  {adObject.passwordNeverExpires ? '✓' : '✗'}
                </BaseTag>
              </div>
            )}

            {adObject.passwordNotRequired !== undefined && (
              <div className="flex items-center justify-between">
                <span className="text-chariot-text-secondary text-xs">
                  Pwd Not Required
                </span>
                <BaseTag
                  className={cn(
                    'h-4 rounded-md py-0 text-xs font-medium',
                    adObject.passwordNotRequired
                      ? 'border-chariot-stroke-critical bg-chariot-status-critical text-chariot-text-critical'
                      : 'border-chariot-stroke bg-chariot-layer1 text-chariot-text-secondary'
                  )}
                  size="xs"
                >
                  {adObject.passwordNotRequired ? '✓' : '✗'}
                </BaseTag>
              </div>
            )}

            {adObject.smartcardRequired !== undefined && (
              <div className="flex items-center justify-between">
                <span className="text-chariot-text-secondary text-xs">
                  Smartcard Required
                </span>
                <BaseTag
                  className={cn(
                    'h-4 rounded-md py-0 text-xs font-medium',
                    adObject.smartcardRequired
                      ? 'border-chariot-stroke-success bg-chariot-status-success text-chariot-text-primary'
                      : 'border-chariot-stroke bg-chariot-layer1 text-chariot-text-secondary'
                  )}
                  size="xs"
                >
                  {adObject.smartcardRequired ? '✓' : '✗'}
                </BaseTag>
              </div>
            )}

            {adObject.dontRequirePreAuth !== undefined && (
              <div className="flex items-center justify-between">
                <span className="text-chariot-text-secondary text-xs">
                  Pre-Auth Not Req
                </span>
                <BaseTag
                  className={cn(
                    'h-4 rounded-md py-0 text-xs font-medium',
                    adObject.dontRequirePreAuth
                      ? 'border-chariot-stroke-warning bg-chariot-status-warning text-chariot-text-primary'
                      : 'border-chariot-stroke bg-chariot-layer1 text-chariot-text-secondary'
                  )}
                  size="xs"
                >
                  {adObject.dontRequirePreAuth ? '✓' : '✗'}
                </BaseTag>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* AD Delegation & Permissions Card */}
      {(adObject.unconstrainedDelegation !== undefined ||
        adObject.trustedToAuth !== undefined ||
        adObject.hasSpn !== undefined) && (
        <div className="bg-chariot-layer0 border-chariot-border rounded-lg border">
          <div className="border-chariot-border border-b px-4 py-3">
            <h4 className="text-chariot-text-primary text-sm font-medium">
              Delegation & Permissions
            </h4>
          </div>
          <div className="space-y-3 p-4">
            {adObject.unconstrainedDelegation !== undefined && (
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-1">
                  <span className="text-chariot-text-secondary text-sm">
                    Unconstrained Delegation
                  </span>
                  <InformationCircleIcon
                    className="text-chariot-text-tertiary h-3.5 w-3.5 cursor-help"
                    title="Allows this account to delegate authentication to any service"
                  />
                </div>
                <BaseTag
                  className={cn(
                    'h-5 rounded-md py-0 font-medium',
                    adObject.unconstrainedDelegation
                      ? 'border-chariot-stroke-critical bg-chariot-status-critical text-chariot-text-critical'
                      : 'border-chariot-stroke bg-chariot-layer1 text-chariot-text-secondary'
                  )}
                  size="xs"
                >
                  {adObject.unconstrainedDelegation
                    ? '⚠ Enabled'
                    : '✓ Disabled'}
                </BaseTag>
              </div>
            )}

            {adObject.trustedToAuth !== undefined && (
              <div className="flex items-start justify-between">
                <span className="text-chariot-text-secondary text-sm">
                  Trusted to Authenticate
                </span>
                <BaseTag
                  className={cn(
                    'h-5 rounded-md py-0 font-medium',
                    adObject.trustedToAuth
                      ? 'border-chariot-stroke-warning bg-chariot-status-warning text-chariot-text-primary'
                      : 'border-chariot-stroke bg-chariot-layer1 text-chariot-text-secondary'
                  )}
                  size="xs"
                >
                  {adObject.trustedToAuth ? '✓ Yes' : '✗ No'}
                </BaseTag>
              </div>
            )}

            {adObject.hasSpn !== undefined && (
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-1">
                  <span className="text-chariot-text-secondary text-sm">
                    Has Service Principal Name
                  </span>
                  <InformationCircleIcon
                    className="text-chariot-text-tertiary h-3.5 w-3.5 cursor-help"
                    title="SPNs are used for Kerberos authentication to services"
                  />
                </div>
                <BaseTag
                  className={cn(
                    'h-5 rounded-md py-0 font-medium',
                    adObject.hasSpn
                      ? 'border-chariot-stroke-info bg-chariot-status-info text-chariot-text-primary'
                      : 'border-chariot-stroke bg-chariot-layer1 text-chariot-text-secondary'
                  )}
                  size="xs"
                >
                  {adObject.hasSpn ? '✓ Yes' : '✗ No'}
                </BaseTag>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Cloud Resource Utilities
const getCloudProvider = (
  asset: CloudResource
): 'aws' | 'azure' | 'gcp' | null => {
  // Check key-based patterns
  if (asset.key?.startsWith('#awsresource#')) return 'aws';
  if (asset.key?.startsWith('#azureresource#')) return 'azure';
  if (asset.key?.startsWith('#gcpresource#')) return 'gcp';

  // Check DNS patterns
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dns = asset.dns || (asset as any).group || '';
  if (dns && typeof dns === 'string') {
    const dnsLower = dns.toLowerCase();
    if (
      dnsLower.includes('.amazonaws.com') ||
      dnsLower.includes('.aws.amazon.com') ||
      dnsLower.includes('.awsglobalaccelerator.com')
    )
      return 'aws';
    if (
      dnsLower.includes('.azure.com') ||
      dnsLower.includes('.azurewebsites.net') ||
      dnsLower.includes('.cloudapp.azure.com') ||
      dnsLower.includes('.azurecontainer.io')
    )
      return 'azure';
    if (
      dnsLower.includes('.googleapis.com') ||
      dnsLower.includes('.googleusercontent.com') ||
      dnsLower.includes('.cloudfunctions.net') ||
      dnsLower.includes('.run.app')
    )
      return 'gcp';
  }

  // Check CloudID for ARN patterns
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cloudId = (asset as any).cloudId || '';
  if (cloudId && typeof cloudId === 'string') {
    if (cloudId.startsWith('arn:aws:')) return 'aws';
    if (
      cloudId.includes('/subscriptions/') &&
      cloudId.includes('/resourceGroups/')
    )
      return 'azure';
    if (cloudId.startsWith('projects/')) return 'gcp';
  }

  // Check CloudService field
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cloudService = (asset as any).cloudService || '';
  if (cloudService && typeof cloudService === 'string') {
    const serviceLower = cloudService.toLowerCase();
    // AWS services
    if (
      [
        'ec2',
        's3',
        'lambda',
        'rds',
        'dynamodb',
        'ecs',
        'eks',
        'route53',
        'cloudfront',
        'elasticbeanstalk',
        'sqs',
        'sns',
        'api-gateway',
      ].includes(serviceLower)
    )
      return 'aws';
    // Azure services
    if (
      [
        'virtual-machines',
        'storage',
        'app-service',
        'sql-database',
        'cosmos-db',
        'aks',
        'functions',
        'cognitive-services',
      ].includes(serviceLower)
    )
      return 'azure';
    // GCP services
    if (
      [
        'compute-engine',
        'cloud-storage',
        'app-engine',
        'cloud-sql',
        'firestore',
        'gke',
        'cloud-functions',
        'cloud-run',
      ].includes(serviceLower)
    )
      return 'gcp';
  }

  return null;
};

// Cloud Provider Icon Component
function CloudProviderIcon({ provider }: { provider: string }) {
  const { currentTheme } = useTheme();
  const providerLower = provider?.toLowerCase();

  if (providerLower === 'aws') {
    return (
      <img
        src={`/icons/${currentTheme}/aws.svg`}
        alt="AWS"
        className="h-5 w-5"
      />
    );
  }

  if (providerLower === 'azure') {
    return (
      <img
        src={`/icons/${currentTheme}/azure.svg`}
        alt="Azure"
        className="h-5 w-5"
      />
    );
  }

  // For GCP and other providers, use the CloudIcon as fallback
  return <CloudIcon className="text-chariot-brand-primary h-5 w-5" />;
}

// Cloud Section 1: Identity & Classification
function CloudIdentitySection({ asset }: { asset: CloudResource }) {
  // Query for additional cloud resource data
  const { data: cloudResourceData } = useGraphQuery({
    enabled: !!asset.key && asset.key.includes('resource'),
    query: {
      node: {
        labels: ['Asset'],
        filters: [
          {
            field: 'key',
            operator: '=',
            value: asset.key,
          },
        ],
      },
    },
  });

  // Extract enhanced cloud properties from query
  let enhancedCloudData = null;
  if (cloudResourceData?.assets?.length) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cloudAsset = cloudResourceData.assets[0] as any;

    // Extract service and region from ARN or CloudId
    let extractedService = null;
    let extractedRegion = null;
    const arnOrCloudId = cloudAsset.cloudId || cloudAsset.name || '';
    if (arnOrCloudId.startsWith('arn:aws:')) {
      const arnParts = arnOrCloudId.split(':');
      if (arnParts.length > 2) {
        extractedService = arnParts[2];
      }
      if (arnParts.length > 3) {
        extractedRegion = arnParts[3]; // Region is the 4th part of ARN
      }
    }

    // Parse properties for Route53 specific data
    let route53Type = null;
    let route53TTL = null;
    if (asset.resourceType?.includes('Route53') && cloudAsset.properties) {
      // Route53 RecordSet properties
      route53Type = cloudAsset.properties.Type || cloudAsset.properties.type;
      route53TTL = cloudAsset.properties.TTL || cloudAsset.properties.ttl;

      // Check for ResourceRecords
      if (cloudAsset.properties.ResourceRecords) {
        // This contains the actual DNS records
      }
    }

    enhancedCloudData = {
      cloudService: cloudAsset.cloudService || extractedService,
      cloudAccount: cloudAsset.cloudAccount,
      cloudRoot: cloudAsset.cloudRoot,
      cloudRegion: cloudAsset.region || extractedRegion,
      displayName: cloudAsset.displayName,
      ips: cloudAsset.ips || [],
      urls: cloudAsset.urls || [],
      origins: cloudAsset.origins || [],
      attackSurface: cloudAsset.attackSurface || [],
      capability: cloudAsset.capability,
      source: cloudAsset.source,
      origin: cloudAsset.origin,
      route53Type,
      route53TTL,
      properties: cloudAsset.properties,
    };
  }

  // Direct access to CloudResource fields - these should be populated by Nebula integration
  const cloudProvider = asset.provider || getCloudProvider(asset);
  const cloudRegion = asset.region || enhancedCloudData?.cloudRegion;
  const cloudResourceType = asset.resourceType;

  // Check if this is a cloud-related IP asset without CloudResource fields
  const isCloudRelatedIPAsset =
    !asset.provider &&
    !asset.resourceType &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ((asset as any).class === 'ipv4' || (asset as any).class === 'ipv6') &&
    isCloudResource(asset);

  // Helper function to extract cloud properties from an asset
  const extractCloudPropertiesFromAsset = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cloudAsset: any
  ): {
    provider: string;
    region: string | null;
    resourceType: string | null;
    accountRef: string | null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    properties: any;
    cloudService: string | null;
    cloudAccount: string | null;
    cloudId: string | null;
    cloudRoot: string | null;
    displayName: string | null;
    parentName: string | null;
    parentKey: string | null;
    resourceArn?: string | null;
    resourceId?: string | null;
    parentOrganization?: string | null;
    childAccounts?: string[] | null;
    additionalAccounts?: string[] | null;
  } | null => {
    if (!cloudAsset) return null;

    const awsAccountMatch = cloudAsset.name?.match(
      /^(\d{12})(?::([a-z]{2}-[a-z]+-\d+))?$/
    );
    if (awsAccountMatch) {
      const accountId = awsAccountMatch[1];
      const regionFromName = awsAccountMatch[2];

      return {
        provider: 'aws',
        region: regionFromName || cloudAsset.region,
        resourceType: 'AWS Account Reference',
        accountRef: accountId,
        properties: cloudAsset.properties || {},
        cloudService: null,
        cloudAccount: null,
        cloudId: cloudAsset.cloudId || cloudAsset.properties?.CloudId,
        cloudRoot: null,
        displayName: cloudAsset.displayName || cloudAsset.name,
        parentName: accountId,
        parentKey: cloudAsset.key,
        resourceArn:
          cloudAsset.properties?.Arn || cloudAsset.properties?.ARN || null,
        resourceId:
          cloudAsset.properties?.ResourceId ||
          cloudAsset.properties?.InstanceId ||
          null,
      };
    }

    let region = cloudAsset.region;
    if (!region && cloudAsset.properties?.Arn) {
      const arnParts = cloudAsset.properties.Arn.split(':');
      if (arnParts.length >= 4) {
        region = arnParts[3];
      }
    }

    // Check if this is a CloudFront distribution
    if (cloudAsset.name?.includes('cloudfront.net')) {
      const distributionId = cloudAsset.name?.split('.')[0]; // Extract distribution ID
      return {
        provider: 'aws',
        region: 'global', // CloudFront is a global service
        resourceType: 'CloudFront Distribution',
        accountRef: cloudAsset.accountRef || cloudAsset.properties?.AccountRef,
        properties: cloudAsset.properties,
        cloudService: 'CloudFront',
        cloudAccount: null,
        cloudId: cloudAsset.cloudId,
        cloudRoot: null,
        displayName: cloudAsset.name,
        parentName: cloudAsset.name,
        parentKey: cloudAsset.key,
        resourceArn:
          cloudAsset.properties?.Arn || cloudAsset.properties?.ARN || null,
        resourceId: distributionId || null,
      };
    }

    // Extract cloud provider from source or key
    let provider = cloudAsset.provider;

    if (!provider && cloudAsset.source) {
      const validProviders = ['aws', 'azure', 'gcp', 'amazon'];
      if (validProviders.includes(cloudAsset.source.toLowerCase())) {
        provider =
          cloudAsset.source.toLowerCase() === 'amazon'
            ? 'aws'
            : cloudAsset.source.toLowerCase();
      }
    }

    if (!provider && cloudAsset.cloudId) {
      if (typeof cloudAsset.cloudId === 'string') {
        if (cloudAsset.cloudId.startsWith('arn:aws:')) {
          provider = 'aws';
        } else if (
          cloudAsset.cloudId.includes('/subscriptions/') ||
          cloudAsset.cloudId.includes('microsoft.')
        ) {
          provider = 'azure';
        } else if (
          cloudAsset.cloudId.includes('//compute.googleapis.com/') ||
          cloudAsset.cloudId.includes('projects/')
        ) {
          provider = 'gcp';
        }
      }
    }

    if (!provider && cloudAsset.cloudService) {
      const service = cloudAsset.cloudService.toLowerCase();
      const awsServices = [
        'ec2',
        's3',
        'lambda',
        'rds',
        'dynamodb',
        'route53',
        'cloudfront',
        'elb',
        'ecs',
        'eks',
      ];
      const azureServices = [
        'virtualmachines',
        'storageaccounts',
        'sqldatabases',
        'appservice',
      ];
      const gcpServices = ['compute', 'storage', 'cloudfunctions', 'cloudsql'];

      if (awsServices.includes(service)) {
        provider = 'aws';
      } else if (azureServices.includes(service)) {
        provider = 'azure';
      } else if (gcpServices.includes(service)) {
        provider = 'gcp';
      }
    }

    if (!provider && cloudAsset.key) {
      if (cloudAsset.key.includes('#awsresource#')) {
        provider = 'aws';
      } else if (cloudAsset.key.includes('#azureresource#')) {
        provider = 'azure';
      } else if (cloudAsset.key.includes('#gcpresource#')) {
        provider = 'gcp';
      }
    }

    // Extract account from ARN or properties
    let accountRef = cloudAsset.accountRef || cloudAsset.properties?.AccountRef;
    if (!accountRef && cloudAsset.properties?.Arn) {
      const arnParts = cloudAsset.properties.Arn.split(':');
      if (arnParts.length >= 5) {
        accountRef = arnParts[4];
      }
    }

    // Extract resource ID from various possible locations
    let resourceId =
      cloudAsset.properties?.ResourceId ||
      cloudAsset.properties?.InstanceId ||
      cloudAsset.properties?.Id;

    // If we have an ARN, try to extract service and resource type
    let cloudService =
      cloudAsset.cloudService || cloudAsset.properties?.ServiceType;
    let resourceType =
      cloudAsset.resourceType || cloudAsset.properties?.ResourceType;
    const arn = cloudAsset.properties?.Arn || cloudAsset.properties?.ARN;
    if (arn && typeof arn === 'string') {
      const arnParts = arn.split(':');
      if (arnParts.length >= 3) {
        cloudService = cloudService || arnParts[2]; // Service is the 3rd part of ARN
      }
      if (arnParts.length >= 6) {
        const resourcePart = arnParts[5];
        // Extract resource type from ARN resource part (e.g., "instance/i-xxxx" -> "instance")
        if (resourcePart.includes('/')) {
          resourceType = resourceType || resourcePart.split('/')[0];
          resourceId = resourceId || resourcePart.split('/')[1];
        } else {
          resourceId = resourceId || resourcePart;
        }
      }
    }

    return {
      provider: provider || getCloudProvider(cloudAsset),
      region: region,
      resourceType: resourceType,
      accountRef: accountRef,
      properties: cloudAsset.properties,
      cloudService: cloudService,
      cloudAccount:
        cloudAsset.cloudAccount ||
        (accountRef !== cloudAsset.cloudRoot ? accountRef : null),
      cloudId: cloudAsset.cloudId,
      cloudRoot:
        cloudAsset.cloudRoot ||
        (cloudAsset.cloudRoot !== accountRef ? cloudAsset.cloudRoot : null),
      displayName: cloudAsset.displayName || cloudAsset.name,
      parentName: cloudAsset.displayName || cloudAsset.name,
      parentKey: cloudAsset.key,
      resourceArn: arn || null,
      resourceId: resourceId || null,
    };
  };

  // Query for ultimate origination sources by following discovery chain all the way back
  // This matches the pattern used in AssetDrawer.tsx for finding cloud resources
  const { data: ultimateOrigins } = useGraphQuery({
    enabled: isCloudRelatedIPAsset,
    tree: true,
    query: {
      node: {
        relationships: [
          {
            label: 'DISCOVERED',
            length: -1, // Follow discovery chain all the way back to find ultimate source
            target: {
              labels: ['Asset'],
              filters: [
                {
                  field: 'key',
                  operator: '=',
                  value: asset.key,
                },
              ],
            },
          },
        ],
      },
    },
  });

  // Also try querying by CloudId if the IP asset has one
  const { data: cloudIdParent } = useGraphQuery({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    enabled: isCloudRelatedIPAsset && !!(asset as any).cloudId,
    query: {
      node: {
        labels: ['Asset'],
        filters: [
          {
            field: 'name', // The ARN is often stored in the name field
            operator: '=',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            value: (asset as any).cloudId || '',
          },
        ],
      },
    },
  });

  const inheritedCloudProperties = (() => {
    if (!isCloudRelatedIPAsset) return null;

    const currentAssetProps = extractCloudPropertiesFromAsset(asset);
    if (
      currentAssetProps?.cloudAccount ||
      currentAssetProps?.cloudService ||
      currentAssetProps?.cloudId
    ) {
      return currentAssetProps;
    }

    if (cloudIdParent?.assets?.length) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const cloudResource = cloudIdParent.assets[0] as any;
      if (cloudResource.key?.includes('resource')) {
        return extractCloudPropertiesFromAsset(cloudResource);
      }
    }

    if (
      ultimateOrigins &&
      Array.isArray(ultimateOrigins) &&
      ultimateOrigins.length > 0
    ) {
      const cloudParents = [];

      for (const treeNode of ultimateOrigins) {
        const originAsset = combineAssetNodeResults(treeNode)[0];

        if (!originAsset || !originAsset.key || originAsset.key === asset.key) {
          continue;
        }

        const isAwsAccount = originAsset.name?.match(
          /^\d{12}(?::[a-z]{2}-[a-z]+-\d+)?$/
        );
        const isCloudResourceKey = originAsset.key?.match(
          /^#(aws|azure|gcp)resource#/
        );
        const isCloudSource =
          originAsset.source === 'aws' ||
          originAsset.source === 'azure' ||
          originAsset.source === 'gcp';
        const isCloudFront =
          originAsset.name?.includes('cloudfront.net') ||
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (originAsset as any).properties?.ServiceType === 'cloudfront';

        if (
          isAwsAccount ||
          isCloudResourceKey ||
          isCloudSource ||
          isCloudFront
        ) {
          cloudParents.push(originAsset);
        }
      }

      if (cloudParents.length > 0) {
        cloudParents.sort((a, b) => {
          const aHasArn =
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (a as any).properties?.Arn || (a as any).properties?.ARN;

          const bHasArn =
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (b as any).properties?.Arn || (b as any).properties?.ARN;
          const aIsResource = a.key?.includes('#awsresource#');
          const bIsResource = b.key?.includes('#awsresource#');
          const aIsCloudFront = a.name?.includes('cloudfront.net');
          const bIsCloudFront = b.name?.includes('cloudfront.net');
          const aIsAccount = a.name?.match(/^\d{12}/);
          const bIsAccount = b.name?.match(/^\d{12}/);

          if (aHasArn && !bHasArn) return -1;
          if (!aHasArn && bHasArn) return 1;

          if (aIsCloudFront && !bIsCloudFront) return -1;
          if (!aIsCloudFront && bIsCloudFront) return 1;

          if (aIsResource && !bIsResource) return -1;
          if (!aIsResource && bIsResource) return 1;

          if (aIsAccount && !bIsAccount) return -1;
          if (!aIsAccount && bIsAccount) return 1;

          return 0;
        });

        const primaryParent = cloudParents[0];
        const awsAccounts = cloudParents.filter(p => p.name?.match(/^\d{12}/));
        const result = extractCloudPropertiesFromAsset(primaryParent);

        if (awsAccounts.length > 1 && result) {
          const otherAccounts = awsAccounts.filter(a => a !== primaryParent);
          result.additionalAccounts = otherAccounts
            .map(a => {
              const match = a.name?.match(
                /^(\d{12})(?::([a-z]{2}-[a-z]+-\d+))?$/
              );
              return match ? match[1] : a.name;
            })
            .filter(Boolean);
        }

        return result;
      }
    }

    return null;
  })();

  let inferredProvider = null;

  if (!asset.provider && !inheritedCloudProperties && asset.dns) {
    const dnsLower = asset.dns.toLowerCase();

    if (dnsLower.includes('.amazonaws.com')) {
      inferredProvider = 'aws';
    } else if (
      dnsLower.includes('.azure.com') ||
      dnsLower.includes('.azurewebsites.net') ||
      dnsLower.includes('.cloudapp.azure.com')
    ) {
      inferredProvider = 'azure';
    } else if (
      dnsLower.includes('.googleapis.com') ||
      dnsLower.includes('.googleusercontent.com') ||
      dnsLower.includes('.run.app')
    ) {
      inferredProvider = 'gcp';
    }
  }

  if (
    !inferredProvider &&
    !inheritedCloudProperties &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (asset as any).cloudId
  ) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cloudId = (asset as any).cloudId;
    if (typeof cloudId === 'string' && cloudId.startsWith('arn:aws:ec2:')) {
      inferredProvider = 'aws';
    }
  }

  if (isCloudRelatedIPAsset && inheritedCloudProperties) {
    const isFromAsset = !!(
      asset.cloudAccount ||
      asset.cloudService ||
      asset.cloudId
    );

    return (
      <div className="space-y-6">
        <div className="bg-chariot-layer0 border-chariot-border rounded-lg border">
          <div className="border-chariot-border border-b px-4 py-3">
            <h4 className="text-chariot-text-primary text-sm font-medium">
              Cloud Properties{isFromAsset ? '' : ' (from parent)'}
            </h4>
          </div>
          <div className="space-y-3 p-4">
            {inheritedCloudProperties.provider && (
              <div className="flex items-start justify-between">
                <span className="text-chariot-text-secondary text-sm">
                  Provider
                </span>
                <div className="flex items-center gap-2">
                  <CloudProviderIcon
                    provider={inheritedCloudProperties.provider}
                  />
                  <span className="text-chariot-text-primary text-sm font-medium">
                    {inheritedCloudProperties.provider.toUpperCase()}
                  </span>
                </div>
              </div>
            )}

            {inheritedCloudProperties.provider &&
              inheritedCloudProperties.region && (
                <div className="border-chariot-border border-b"></div>
              )}

            {inheritedCloudProperties.region && (
              <div className="flex items-start justify-between">
                <span className="text-chariot-text-secondary text-sm">
                  Region
                </span>
                <span className="text-chariot-text-primary text-sm font-medium">
                  {inheritedCloudProperties.region}
                </span>
              </div>
            )}

            {inheritedCloudProperties.region &&
              inheritedCloudProperties.resourceType && (
                <div className="border-chariot-border border-b"></div>
              )}

            {inheritedCloudProperties.resourceType && (
              <div className="flex items-start justify-between">
                <span className="text-chariot-text-secondary text-sm">
                  Resource Type
                </span>
                <span className="text-chariot-text-primary text-sm font-medium">
                  {inheritedCloudProperties.resourceType}
                </span>
              </div>
            )}

            {inheritedCloudProperties.accountRef && (
              <>
                {(inheritedCloudProperties.provider ||
                  inheritedCloudProperties.region ||
                  inheritedCloudProperties.resourceType) && (
                  <div className="border-chariot-border border-b"></div>
                )}
                <div className="flex items-start justify-between">
                  <span className="text-chariot-text-secondary text-sm">
                    Account ID
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-chariot-text-primary font-mono text-sm font-medium">
                      {inheritedCloudProperties.accountRef}
                    </span>
                    <CopyToClipboard
                      textToCopy={String(inheritedCloudProperties.accountRef)}
                      alwaysShowCopyIcon
                    />
                  </div>
                </div>
              </>
            )}

            {inheritedCloudProperties.parentName && (
              <>
                <div className="border-chariot-border border-b"></div>
                <div className="flex items-start justify-between">
                  <span className="text-chariot-text-secondary text-sm">
                    Parent Resource
                  </span>
                  <span className="text-chariot-text-primary max-w-xs text-right text-sm font-medium break-all">
                    {inheritedCloudProperties.parentName}
                  </span>
                </div>
              </>
            )}

            {'additionalAccounts' in inheritedCloudProperties &&
              inheritedCloudProperties.additionalAccounts &&
              inheritedCloudProperties.additionalAccounts.length > 0 && (
                <>
                  <div className="border-chariot-border border-b"></div>
                  <div className="flex items-start justify-between">
                    <span className="text-chariot-text-secondary text-sm">
                      Related AWS Accounts
                    </span>
                    <span className="text-chariot-text-primary text-sm font-medium">
                      {inheritedCloudProperties.additionalAccounts.join(', ')}
                    </span>
                  </div>
                </>
              )}

            {inheritedCloudProperties.displayName &&
              inheritedCloudProperties.displayName !==
                inheritedCloudProperties.parentName && (
                <>
                  <div className="border-chariot-border border-b"></div>
                  <div className="flex items-start justify-between">
                    <span className="text-chariot-text-secondary text-sm">
                      Display Name
                    </span>
                    <span className="text-chariot-text-primary text-sm font-medium">
                      {inheritedCloudProperties.displayName}
                    </span>
                  </div>
                </>
              )}
          </div>
        </div>

        {/* Resource Details Card - if we have ARN, Resource ID, or Cloud ID */}
        {(('resourceArn' in inheritedCloudProperties &&
          inheritedCloudProperties.resourceArn) ||
          ('resourceId' in inheritedCloudProperties &&
            inheritedCloudProperties.resourceId) ||
          inheritedCloudProperties.cloudId ||
          inheritedCloudProperties.cloudService ||
          inheritedCloudProperties.cloudRoot ||
          inheritedCloudProperties.cloudAccount) && (
          <div className="bg-chariot-layer0 border-chariot-border rounded-lg border">
            <div className="border-chariot-border border-b px-4 py-3">
              <h4 className="text-chariot-text-primary text-sm font-medium">
                Resource Details
              </h4>
            </div>
            <div className="space-y-3 p-4">
              {'resourceArn' in inheritedCloudProperties &&
                inheritedCloudProperties.resourceArn && (
                  <div className="flex items-start justify-between">
                    <span className="text-chariot-text-secondary text-sm">
                      ARN
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-chariot-text-primary max-w-xs text-right font-mono text-sm text-xs font-medium break-all">
                        {inheritedCloudProperties.resourceArn}
                      </span>
                      <CopyToClipboard
                        textToCopy={inheritedCloudProperties.resourceArn}
                        alwaysShowCopyIcon
                      />
                    </div>
                  </div>
                )}

              {'resourceId' in inheritedCloudProperties &&
                inheritedCloudProperties.resourceId && (
                  <>
                    {'resourceArn' in inheritedCloudProperties &&
                      inheritedCloudProperties.resourceArn && (
                        <div className="border-chariot-border border-b"></div>
                      )}
                    <div className="flex items-start justify-between">
                      <span className="text-chariot-text-secondary text-sm">
                        Resource ID
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-chariot-text-primary font-mono text-sm font-medium">
                          {inheritedCloudProperties.resourceId}
                        </span>
                        <CopyToClipboard
                          textToCopy={inheritedCloudProperties.resourceId}
                          alwaysShowCopyIcon
                        />
                      </div>
                    </div>
                  </>
                )}

              {inheritedCloudProperties.cloudService &&
                inheritedCloudProperties.cloudService !==
                  inheritedCloudProperties.provider && (
                  <>
                    {(('resourceArn' in inheritedCloudProperties &&
                      inheritedCloudProperties.resourceArn) ||
                      ('resourceId' in inheritedCloudProperties &&
                        inheritedCloudProperties.resourceId)) && (
                      <div className="border-chariot-border border-b"></div>
                    )}
                    <div className="flex items-start justify-between">
                      <span className="text-chariot-text-secondary text-sm">
                        Cloud Service
                      </span>
                      <span className="text-chariot-text-primary text-sm font-medium">
                        {inheritedCloudProperties.cloudService}
                      </span>
                    </div>
                  </>
                )}

              {inheritedCloudProperties.cloudRoot &&
                inheritedCloudProperties.cloudRoot !==
                  inheritedCloudProperties.accountRef && (
                  <>
                    <div className="border-chariot-border border-b"></div>
                    <div className="flex items-start justify-between">
                      <span className="text-chariot-text-secondary text-sm">
                        Cloud Root
                      </span>
                      <span className="text-chariot-text-primary text-sm font-medium">
                        {inheritedCloudProperties.cloudRoot}
                      </span>
                    </div>
                  </>
                )}

              {inheritedCloudProperties.cloudAccount &&
                inheritedCloudProperties.cloudAccount !==
                  inheritedCloudProperties.accountRef && (
                  <>
                    <div className="border-chariot-border border-b"></div>
                    <div className="flex items-start justify-between">
                      <span className="text-chariot-text-secondary text-sm">
                        Cloud Account
                      </span>
                      <span className="text-chariot-text-primary text-sm font-medium">
                        {inheritedCloudProperties.cloudAccount}
                      </span>
                    </div>
                  </>
                )}

              {inheritedCloudProperties.cloudId && (
                <>
                  <div className="border-chariot-border border-b"></div>
                  <div className="flex items-start justify-between">
                    <span className="text-chariot-text-secondary text-sm">
                      Cloud ID
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-chariot-text-primary max-w-xs text-right font-mono text-sm text-xs font-medium break-all">
                        {inheritedCloudProperties.cloudId}
                      </span>
                      <CopyToClipboard
                        textToCopy={inheritedCloudProperties.cloudId}
                        alwaysShowCopyIcon
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Organization Hierarchy Card - if we have parent org or child accounts */}
        {(('parentOrganization' in inheritedCloudProperties &&
          inheritedCloudProperties.parentOrganization) ||
          ('childAccounts' in inheritedCloudProperties &&
            inheritedCloudProperties.childAccounts &&
            inheritedCloudProperties.childAccounts.length > 0)) && (
          <div className="bg-chariot-layer0 border-chariot-border rounded-lg border">
            <div className="border-chariot-border border-b px-4 py-3">
              <h4 className="text-chariot-text-primary text-sm font-medium">
                Organization Hierarchy
              </h4>
            </div>
            <div className="space-y-3 p-4">
              {'parentOrganization' in inheritedCloudProperties &&
                inheritedCloudProperties.parentOrganization && (
                  <div className="flex items-start justify-between">
                    <span className="text-chariot-text-secondary text-sm">
                      Parent AWS Organization
                    </span>
                    <span className="text-chariot-text-primary font-mono text-sm font-medium">
                      {inheritedCloudProperties.parentOrganization}
                    </span>
                  </div>
                )}

              {'childAccounts' in inheritedCloudProperties &&
                inheritedCloudProperties.childAccounts &&
                inheritedCloudProperties.childAccounts.length > 0 && (
                  <>
                    {'parentOrganization' in inheritedCloudProperties &&
                      inheritedCloudProperties.parentOrganization && (
                        <div className="border-chariot-border border-b"></div>
                      )}
                    <div className="flex items-start justify-between">
                      <span className="text-chariot-text-secondary text-sm">
                        Child Accounts
                      </span>
                      <span className="text-chariot-text-primary text-sm font-medium">
                        {inheritedCloudProperties.childAccounts.join(', ')}
                      </span>
                    </div>
                  </>
                )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Fallback to generic message if no inherited properties found
  if (isCloudRelatedIPAsset) {
    return (
      <div>
        <div className="bg-chariot-layer1 mb-4 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="text-chariot-text-secondary">
              <GlobeAltIcon className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <p className="text-chariot-text-primary text-sm font-medium">
                Cloud-Associated Network Asset
              </p>
              <p className="text-chariot-text-secondary mt-1 text-sm">
                This IP address ({asset.name}) is associated with a cloud
                resource but is stored as a network asset. Cloud-specific
                details are available on the parent EC2 instance or cloud
                resource.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Get ARN/Resource Path based on provider
  let resourcePath;
  if (cloudProvider === 'aws') {
    resourcePath = asset.properties?.arn || asset.properties?.ARN || asset.name;
  } else if (cloudProvider === 'azure') {
    resourcePath = asset.properties?.id || asset.properties?.resourceId;
  } else if (cloudProvider === 'gcp') {
    resourcePath = asset.properties?.selfLink || asset.properties?.id;
  }

  // Combine data from direct fields and enhanced query
  const cloudService =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    enhancedCloudData?.cloudService || (asset as any).cloudService;
  const cloudRoot =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    enhancedCloudData?.cloudRoot || (asset as any).cloudRoot;
  const displayName =
    enhancedCloudData?.displayName ||
    getDisplayIdentifierWithFallback(asset, '');

  const allProperties: PropertyItem[] = [
    {
      label: 'Provider',
      value: cloudProvider ? cloudProvider.toUpperCase() : undefined,
    },
    { label: '🌍 Region', value: cloudRegion },
    { label: 'Resource Type', value: cloudResourceType },
    { label: 'Display Name', value: displayName },
    { label: 'Resource Name', value: asset.name },
    {
      label: 'Resource ID',
      value: asset.identifier || asset.properties?.instanceId,
    },
    { label: 'Cloud Service', value: cloudService },
    { label: 'Cloud Root', value: cloudRoot },
    { label: 'Cloud Account', value: enhancedCloudData?.cloudAccount },
    { label: 'Account', value: asset.accountRef },
    {
      label: 'Status',
      value:
        asset.status || asset.properties?.state || asset.properties?.status,
    },
    { label: 'ARN/Resource Path', value: resourcePath },
    // Route53 specific fields
    ...(enhancedCloudData?.route53Type
      ? [
          { label: 'Record Type', value: enhancedCloudData.route53Type },
          {
            label: 'TTL',
            value: enhancedCloudData.route53TTL
              ? formatTTL(enhancedCloudData.route53TTL)
              : undefined,
          },
        ]
      : []),
    // Discovery metadata
    { label: 'Origins', value: enhancedCloudData?.origins?.join(', ') },
    {
      label: 'Attack Surface',
      value: enhancedCloudData?.attackSurface?.join(', '),
    },
    { label: 'Discovery Source', value: enhancedCloudData?.source },
    { label: 'Discovery Capability', value: enhancedCloudData?.capability },
    { label: 'First Discovered By', value: enhancedCloudData?.origin },
    // Network endpoints
    {
      label: 'IP Addresses',
      value: enhancedCloudData?.ips?.length
        ? enhancedCloudData.ips.join(', ')
        : undefined,
    },
    {
      label: 'URLs/Endpoints',
      value: enhancedCloudData?.urls?.length
        ? enhancedCloudData.urls.join(', ')
        : undefined,
    },
    {
      label: 'Labels',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      value: (asset as any).labels?.length
        ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (asset as any).labels.join(', ')
        : undefined,
    },
    {
      label: 'Tags',
      value: asset.properties?.tags
        ? JSON.stringify(asset.properties.tags)
        : undefined,
    },
    { label: 'Username/Owner', value: asset.username },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    { label: 'Class', value: (asset as any).class },
  ].filter(p => p.value !== undefined && p.value !== null);

  // If we have provider and region, highlight them at the top
  if (cloudProvider && cloudRegion) {
    return (
      <div className="space-y-6">
        {/* Header with cloud provider indicator */}
        <div className="bg-chariot-layer1 border-chariot-border rounded-lg border p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-chariot-brand-primary/10 rounded-lg p-2">
                <CloudProviderIcon provider={cloudProvider} />
              </div>
              <div>
                <p className="text-chariot-text-primary text-lg font-semibold">
                  {cloudProvider.toUpperCase()} Resource
                </p>
                {cloudResourceType && (
                  <div className="mt-1 flex items-center gap-2">
                    <p className="text-chariot-text-primary text-base">
                      {cloudResourceType}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Cloud Identity Card */}
        <div className="bg-chariot-layer0 border-chariot-border rounded-lg border">
          <div className="border-chariot-border border-b px-4 py-3">
            <h4 className="text-chariot-text-primary text-sm font-medium">
              Cloud Identity
            </h4>
          </div>
          <div className="space-y-3 p-4">
            {cloudProvider && (
              <div className="flex items-start justify-between">
                <span className="text-chariot-text-secondary text-sm">
                  Provider
                </span>
                <div className="flex items-center gap-2">
                  <CloudProviderIcon provider={cloudProvider} />
                  <span className="text-chariot-text-primary text-sm font-medium">
                    {cloudProvider.toUpperCase()}
                  </span>
                </div>
              </div>
            )}

            {cloudProvider && cloudRegion && (
              <div className="border-chariot-border border-b"></div>
            )}

            {cloudRegion && (
              <div className="flex items-start justify-between">
                <span className="text-chariot-text-secondary text-sm">
                  Region
                </span>
                <span className="text-chariot-text-primary text-sm font-medium">
                  {cloudRegion}
                </span>
              </div>
            )}

            {cloudRegion && cloudResourceType && (
              <div className="border-chariot-border border-b"></div>
            )}

            {cloudResourceType && (
              <div className="flex items-start justify-between">
                <span className="text-chariot-text-secondary text-sm">
                  Resource Type
                </span>
                <span className="text-chariot-text-primary text-sm font-medium">
                  {cloudResourceType}
                </span>
              </div>
            )}

            {(asset.identifier || asset.properties?.instanceId) && (
              <>
                {cloudResourceType && (
                  <div className="border-chariot-border border-b"></div>
                )}
                <div className="flex items-start justify-between">
                  <span className="text-chariot-text-secondary text-sm">
                    Resource ID
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-chariot-text-primary font-mono text-sm text-xs font-medium">
                      {asset.identifier || asset.properties?.instanceId}
                    </span>
                    <CopyToClipboard
                      textToCopy={
                        asset.identifier || asset.properties?.instanceId || ''
                      }
                      alwaysShowCopyIcon
                    />
                  </div>
                </div>
              </>
            )}

            {asset.name && (
              <>
                <div className="border-chariot-border border-b"></div>
                <div className="flex items-start justify-between">
                  <span className="text-chariot-text-secondary text-sm">
                    Resource Name
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-chariot-text-primary max-w-xs text-right text-sm font-medium break-all">
                      {asset.name}
                    </span>
                    <CopyToClipboard
                      textToCopy={asset.name}
                      alwaysShowCopyIcon
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Cloud Resource Details Card */}
        <div className="bg-chariot-layer0 border-chariot-border rounded-lg border">
          <div className="border-chariot-border border-b px-4 py-3">
            <h4 className="text-chariot-text-primary text-sm font-medium">
              Resource Details
            </h4>
          </div>
          <div className="space-y-3 p-4">
            {displayName && displayName !== asset.name && (
              <div className="flex items-start justify-between">
                <span className="text-chariot-text-secondary text-sm">
                  Display Name
                </span>
                <span className="text-chariot-text-primary text-sm font-medium">
                  {displayName}
                </span>
              </div>
            )}

            {cloudService && (
              <>
                {displayName && displayName !== asset.name && (
                  <div className="border-chariot-border border-b"></div>
                )}
                <div className="flex items-start justify-between">
                  <span className="text-chariot-text-secondary text-sm">
                    Cloud Service
                  </span>
                  <span className="text-chariot-text-primary text-sm font-medium">
                    {cloudService}
                  </span>
                </div>
              </>
            )}

            {(asset.status ||
              asset.properties?.state ||
              asset.properties?.status) && (
              <>
                {(displayName || cloudService) && (
                  <div className="border-chariot-border border-b"></div>
                )}
                <div className="flex items-start justify-between">
                  <span className="text-chariot-text-secondary text-sm">
                    Status
                  </span>
                  <span className="text-chariot-text-primary text-sm font-medium">
                    {asset.status ||
                      asset.properties?.state ||
                      asset.properties?.status}
                  </span>
                </div>
              </>
            )}

            {resourcePath && (
              <>
                <div className="border-chariot-border border-b"></div>
                <div className="flex items-start justify-between">
                  <span className="text-chariot-text-secondary text-sm">
                    ARN/Resource Path
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-chariot-text-primary max-w-xs text-right font-mono text-sm text-xs font-medium break-all">
                      {resourcePath}
                    </span>
                    <CopyToClipboard
                      textToCopy={resourcePath}
                      alwaysShowCopyIcon
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Cloud Account & Organization Card */}
        <div className="bg-chariot-layer0 border-chariot-border rounded-lg border">
          <div className="border-chariot-border border-b px-4 py-3">
            <h4 className="text-chariot-text-primary text-sm font-medium">
              Account & Organization
            </h4>
          </div>
          <div className="space-y-3 p-4">
            {asset.accountRef && (
              <div className="flex items-start justify-between">
                <span className="text-chariot-text-secondary text-sm">
                  Account ID
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-chariot-text-primary font-mono text-sm font-medium">
                    {asset.accountRef}
                  </span>
                  <CopyToClipboard
                    textToCopy={String(asset.accountRef)}
                    alwaysShowCopyIcon
                  />
                </div>
              </div>
            )}

            {cloudRoot && cloudRoot !== asset.accountRef && (
              <>
                {asset.accountRef && (
                  <div className="border-chariot-border border-b"></div>
                )}
                <div className="flex items-start justify-between">
                  <span className="text-chariot-text-secondary text-sm">
                    Cloud Root
                  </span>
                  <span className="text-chariot-text-primary text-sm font-medium">
                    {cloudRoot}
                  </span>
                </div>
              </>
            )}

            {enhancedCloudData?.cloudAccount &&
              enhancedCloudData.cloudAccount !== asset.accountRef && (
                <>
                  {(asset.accountRef || cloudRoot) && (
                    <div className="border-chariot-border border-b"></div>
                  )}
                  <div className="flex items-start justify-between">
                    <span className="text-chariot-text-secondary text-sm">
                      Cloud Account
                    </span>
                    <span className="text-chariot-text-primary text-sm font-medium">
                      {enhancedCloudData.cloudAccount}
                    </span>
                  </div>
                </>
              )}

            {asset.username && (
              <>
                {(asset.accountRef ||
                  cloudRoot ||
                  enhancedCloudData?.cloudAccount) && (
                  <div className="border-chariot-border border-b"></div>
                )}
                <div className="flex items-start justify-between">
                  <span className="text-chariot-text-secondary text-sm">
                    Username/Owner
                  </span>
                  <span className="text-chariot-text-primary text-sm font-medium">
                    {asset.username}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Cloud Network & Endpoints Card - if there are IPs or URLs */}
        {(enhancedCloudData?.ips?.length ||
          enhancedCloudData?.urls?.length) && (
          <div className="bg-chariot-layer0 border-chariot-border rounded-lg border">
            <div className="border-chariot-border border-b px-4 py-3">
              <h4 className="text-chariot-text-primary text-sm font-medium">
                Network & Endpoints
              </h4>
            </div>
            <div className="space-y-3 p-4">
              {enhancedCloudData?.ips?.length > 0 && (
                <div className="flex items-start justify-between">
                  <span className="text-chariot-text-secondary text-sm">
                    IP Addresses
                  </span>
                  <div className="text-right">
                    {enhancedCloudData.ips.map((ip: string, idx: number) => (
                      <div
                        key={idx}
                        className="mb-1 flex items-center justify-end gap-2"
                      >
                        <span className="text-chariot-text-primary font-mono text-sm font-medium">
                          {ip}
                        </span>
                        <CopyToClipboard textToCopy={ip} alwaysShowCopyIcon />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {enhancedCloudData?.ips?.length > 0 &&
                enhancedCloudData?.urls?.length > 0 && (
                  <div className="border-chariot-border border-b"></div>
                )}

              {enhancedCloudData?.urls?.length > 0 && (
                <div className="flex items-start justify-between">
                  <span className="text-chariot-text-secondary text-sm">
                    URLs/Endpoints
                  </span>
                  <div className="text-right">
                    {enhancedCloudData.urls.map((url: string, idx: number) => (
                      <div
                        key={idx}
                        className="mb-1 flex items-center justify-end gap-2"
                      >
                        <span className="text-chariot-text-primary max-w-xs text-right text-sm font-medium break-all">
                          {url}
                        </span>
                        <CopyToClipboard textToCopy={url} alwaysShowCopyIcon />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Cloud Discovery Metadata Card */}
        {(enhancedCloudData?.origins?.length ||
          enhancedCloudData?.source ||
          enhancedCloudData?.capability) && (
          <div className="bg-chariot-layer0 border-chariot-border rounded-lg border">
            <div className="border-chariot-border border-b px-4 py-3">
              <h4 className="text-chariot-text-primary text-sm font-medium">
                Discovery Metadata
              </h4>
            </div>
            <div className="space-y-3 p-4">
              {enhancedCloudData?.source && (
                <div className="flex items-start justify-between">
                  <span className="text-chariot-text-secondary text-sm">
                    Discovery Source
                  </span>
                  <span className="text-chariot-text-primary text-sm font-medium">
                    {enhancedCloudData.source}
                  </span>
                </div>
              )}

              {enhancedCloudData?.capability && (
                <>
                  {enhancedCloudData?.source && (
                    <div className="border-chariot-border border-b"></div>
                  )}
                  <div className="flex items-start justify-between">
                    <span className="text-chariot-text-secondary text-sm">
                      Discovery Capability
                    </span>
                    <span className="text-chariot-text-primary text-sm font-medium">
                      {enhancedCloudData.capability}
                    </span>
                  </div>
                </>
              )}

              {enhancedCloudData?.origin && (
                <>
                  {(enhancedCloudData?.source ||
                    enhancedCloudData?.capability) && (
                    <div className="border-chariot-border border-b"></div>
                  )}
                  <div className="flex items-start justify-between">
                    <span className="text-chariot-text-secondary text-sm">
                      First Discovered By
                    </span>
                    <span className="text-chariot-text-primary text-sm font-medium">
                      {enhancedCloudData.origin}
                    </span>
                  </div>
                </>
              )}

              {enhancedCloudData?.origins?.length > 0 && (
                <>
                  {(enhancedCloudData?.source ||
                    enhancedCloudData?.capability ||
                    enhancedCloudData?.origin) && (
                    <div className="border-chariot-border border-b"></div>
                  )}
                  <div className="flex items-start justify-between">
                    <span className="text-chariot-text-secondary text-sm">
                      Origins
                    </span>
                    <span className="text-chariot-text-primary text-sm font-medium">
                      {enhancedCloudData.origins.join(', ')}
                    </span>
                  </div>
                </>
              )}

              {enhancedCloudData?.attackSurface?.length > 0 && (
                <>
                  <div className="border-chariot-border border-b"></div>
                  <div className="flex items-start justify-between">
                    <span className="text-chariot-text-secondary text-sm">
                      Attack Surface
                    </span>
                    <span className="text-chariot-text-primary text-sm font-medium">
                      {enhancedCloudData.attackSurface.join(', ')}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Special section for Route53 resources */}
        {cloudResourceType?.includes('Route53') &&
          enhancedCloudData?.properties && (
            <div className="bg-chariot-layer0 border-chariot-border rounded-lg border">
              <div className="border-chariot-border border-b px-4 py-3">
                <h4 className="text-chariot-text-primary text-sm font-medium">
                  Route53 Configuration
                </h4>
              </div>
              <div className="space-y-3 p-4">
                {enhancedCloudData.properties.ResourceRecords && (
                  <>
                    <div className="flex items-start justify-between">
                      <span className="text-chariot-text-secondary text-sm">
                        Resource Records
                      </span>
                    </div>
                    <div className="ml-4 space-y-2">
                      {Array.isArray(
                        enhancedCloudData.properties.ResourceRecords
                      ) &&
                        enhancedCloudData.properties.ResourceRecords.map(
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          (record: any, idx: number) => (
                            <div
                              key={idx}
                              className="flex items-start justify-between"
                            >
                              <span className="text-chariot-text-secondary text-sm">
                                Record {idx + 1}
                              </span>
                              <span className="text-chariot-text-primary font-mono text-sm font-medium">
                                {record.Value || JSON.stringify(record)}
                              </span>
                            </div>
                          )
                        )}
                    </div>
                  </>
                )}

                {enhancedCloudData.properties.AliasTarget && (
                  <>
                    {enhancedCloudData.properties.ResourceRecords && (
                      <div className="border-chariot-border border-b"></div>
                    )}
                    <div className="flex items-start justify-between">
                      <span className="text-chariot-text-secondary text-sm">
                        Alias Target
                      </span>
                    </div>
                    <div className="ml-4 space-y-2">
                      <div className="flex items-start justify-between">
                        <span className="text-chariot-text-secondary text-sm">
                          DNS Name
                        </span>
                        <span className="text-chariot-text-primary text-sm font-medium">
                          {enhancedCloudData.properties.AliasTarget.DNSName}
                        </span>
                      </div>
                      <div className="flex items-start justify-between">
                        <span className="text-chariot-text-secondary text-sm">
                          Hosted Zone
                        </span>
                        <span className="text-chariot-text-primary text-sm font-medium">
                          {
                            enhancedCloudData.properties.AliasTarget
                              .HostedZoneId
                          }
                        </span>
                      </div>
                      <div className="flex items-start justify-between">
                        <span className="text-chariot-text-secondary text-sm">
                          Evaluate Target Health
                        </span>
                        <span className="text-chariot-text-primary text-sm font-medium">
                          {String(
                            enhancedCloudData.properties.AliasTarget
                              .EvaluateTargetHealth
                          )}
                        </span>
                      </div>
                    </div>
                  </>
                )}

                {enhancedCloudData.route53Type && (
                  <>
                    {(enhancedCloudData.properties.ResourceRecords ||
                      enhancedCloudData.properties.AliasTarget) && (
                      <div className="border-chariot-border border-b"></div>
                    )}
                    <div className="flex items-start justify-between">
                      <span className="text-chariot-text-secondary text-sm">
                        Record Type
                      </span>
                      <span className="text-chariot-text-primary text-sm font-medium">
                        {enhancedCloudData.route53Type}
                      </span>
                    </div>
                  </>
                )}

                {enhancedCloudData.route53TTL && (
                  <div className="flex items-start justify-between">
                    <span className="text-chariot-text-secondary text-sm">
                      TTL
                    </span>
                    <span className="text-chariot-text-primary text-sm font-medium">
                      {formatTTL(enhancedCloudData.route53TTL)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

        {/* Cloud Tags & Labels Card */}
        {((asset.properties?.tags &&
          Object.keys(asset.properties.tags).length > 0) ||
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (asset as any).labels?.length > 0) && (
          <div className="bg-chariot-layer0 border-chariot-border rounded-lg border">
            <div className="border-chariot-border border-b px-4 py-3">
              <h4 className="text-chariot-text-primary text-sm font-medium">
                Tags & Labels
              </h4>
            </div>
            <div className="space-y-3 p-4">
              {asset.properties?.tags &&
                Object.keys(asset.properties.tags).length > 0 && (
                  <div className="space-y-2">
                    <span className="text-chariot-text-secondary text-sm">
                      Resource Tags
                    </span>
                    <div className="ml-4 space-y-1">
                      {Object.entries(asset.properties.tags).map(
                        ([key, value], idx) => (
                          <div
                            key={idx}
                            className="flex items-start justify-between"
                          >
                            <span className="text-chariot-text-secondary text-sm">
                              {key}
                            </span>
                            <span className="text-chariot-text-primary text-sm font-medium">
                              {String(value)}
                            </span>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {(asset as any).labels?.length > 0 && (
                <>
                  {asset.properties?.tags &&
                    Object.keys(asset.properties.tags).length > 0 && (
                      <div className="border-chariot-border border-b"></div>
                    )}
                  <div className="flex items-start justify-between">
                    <span className="text-chariot-text-secondary text-sm">
                      Labels
                    </span>
                    <span className="text-chariot-text-primary text-sm font-medium">
                      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                      {(asset as any).labels.join(', ')}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Additional properties section for debugging */}
        {enhancedCloudData?.properties &&
          Object.keys(enhancedCloudData.properties).length > 0 && (
            <details className="mt-4">
              <summary className="text-chariot-text-secondary hover:text-chariot-text-primary cursor-pointer text-sm">
                View All Properties (
                {Object.keys(enhancedCloudData.properties).length} fields)
              </summary>
              <div className="bg-chariot-layer0 mt-2 max-h-96 overflow-y-auto rounded-lg p-3">
                <pre className="text-chariot-text-secondary font-mono text-xs">
                  {JSON.stringify(enhancedCloudData.properties, null, 2)}
                </pre>
              </div>
            </details>
          )}
      </div>
    );
  }

  // Fallback to standard display if no provider/region
  return (
    <div>
      {allProperties.map((property, index) => (
        <PropertyDisplay key={index} {...property} />
      ))}
      {allProperties.length === 0 && (
        <span>No identity information available</span>
      )}
    </div>
  );
}

// Domain Section Component
function AssetDomainSection({ asset }: { asset: Asset }) {
  const [activeTab, setActiveTab] = useState<'details' | 'history'>('details');
  const dns = asset.group;
  const domain = psl.get(dns || '');
  const isDomain = psl.isValid(dns || '');

  const { data: details, status: detailsStatus } = useGetFile(
    {
      name: `whois/${domain}`,
    },
    { enabled: isDomain }
  );
  const { data: history, status: historyStatus } = useGetFile(
    {
      name: `whois-history/${domain}`,
    },
    { enabled: isDomain }
  );

  const detailsValue =
    typeof details === 'string' ? details : JSON.stringify(details);
  const historyExists = history && history.length > 0;

  if (!dns || typeof dns !== 'string') {
    return <div>Invalid domain</div>;
  }

  if (!isDomain) {
    return (
      <NoData
        icon={
          <SoundRecognitionSearchIcon className="text-chariot-stroke text-[100px]" />
        }
        title="No Domain Linked to this Asset"
      />
    );
  }

  return (
    <div>
      {/* Tab buttons */}
      <div className="border-chariot-stroke mb-4 flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('details')}
          className={cn(
            'px-4 py-2 text-sm font-medium transition-colors',
            activeTab === 'details'
              ? 'border-chariot-brand-primary text-chariot-text-primary border-b-2'
              : 'text-chariot-text-secondary hover:text-chariot-text-primary'
          )}
        >
          Domain Details
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={cn(
            'px-4 py-2 text-sm font-medium transition-colors',
            activeTab === 'history'
              ? 'border-chariot-brand-primary text-chariot-text-primary border-b-2'
              : 'text-chariot-text-secondary hover:text-chariot-text-primary'
          )}
        >
          Domain History
        </button>
      </div>

      {isDomain &&
      (detailsStatus === 'pending' || historyStatus === 'pending') ? (
        <Spinner centered size="xl" />
      ) : (
        <>
          {activeTab === 'details' && (
            <div className="h-full">
              {detailsValue && <pre>{detailsValue}</pre>}
              {!detailsValue && (
                <NoData
                  icon={
                    <SoundRecognitionSearchIcon className="text-chariot-stroke text-[100px]" />
                  }
                  title="No Details on this Domain"
                />
              )}
            </div>
          )}
          {activeTab === 'history' && historyStatus === 'success' && (
            <>
              {!historyExists && (
                <NoData
                  icon={
                    <SoundRecognitionSearchIcon className="text-chariot-stroke text-[100px]" />
                  }
                  title="No History on this Domain"
                />
              )}
              {historyExists &&
                (history || [])?.map((data: string, i: number) => (
                  <div key={i} className="">
                    <MarkdownPreview
                      source={`\n\`\`\`json\n${JSON.stringify(data, null, 2)}\n\`\`\`\n`}
                    />
                  </div>
                ))}
            </>
          )}
        </>
      )}
    </div>
  );
}
